import { useState, useRef, useEffect } from 'react';

const priorityBadge = {
  Low: 'bg-success',
  Medium: 'bg-warning text-dark',
  High: 'bg-danger',
};

const statusBadge = {
  'Not Started': 'bg-secondary',
  Preparing: 'bg-orange-status',
  'In Progress': 'bg-dark-blue',
  'On Hold': 'bg-warning text-dark',
  Completed: 'bg-success',
};

const PRIORITY_OPTIONS = ['Low', 'Medium', 'High'];
const STATUS_OPTIONS = ['Not Started', 'Preparing', 'In Progress', 'On Hold', 'Completed'];

function formatDateLocal(dateStr) {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.slice(0, 10).split('-');
  return new Date(year, month - 1, day).toLocaleDateString();
}

function InlineSelect({ options, value, onCommit, onCancel }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <select
      ref={ref}
      className="form-select form-select-sm"
      style={{ width: 'auto', display: 'inline-block' }}
      defaultValue={value || ''}
      onChange={(e) => onCommit(e.target.value)}
      onBlur={onCancel}
      onClick={(e) => e.stopPropagation()}
    >
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function InlineText({ value, onCommit, onCancel }) {
  const ref = useRef(null);
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const finish = () => {
    const next = draft.trim();
    if (!next || next === (value || '')) onCancel();
    else onCommit(next);
  };
  return (
    <input
      ref={ref}
      type="text"
      className="form-control form-control-sm fw-semibold"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={finish}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
        if (e.key === 'Enter') e.target.blur();
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

function InlineDate({ value, onCommit, onCancel }) {
  const ref = useRef(null);
  const [draft, setDraft] = useState(value ? value.slice(0, 10) : '');
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <input
      ref={ref}
      type="date"
      className="form-control form-control-sm"
      style={{ width: 'auto', display: 'inline-block' }}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const next = draft || null;
        const cur = value ? value.slice(0, 10) : null;
        if (next === cur) onCancel();
        else onCommit(next);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
        if (e.key === 'Enter') e.target.blur();
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

export default function TaskItem({
  task,
  onEdit,
  onDelete,
  onApplyField,
  showFolder,
  selectable,
  selected,
  selectedIds,
  onToggleSelect,
}) {
  const [editing, setEditing] = useState(null);

  const stop = (e) => e.stopPropagation();

  const apply = (field) => (val) => {
    setEditing(null);
    if (onApplyField) onApplyField(task.id, field, val);
  };

  return (
    <tr
      style={onEdit ? { cursor: 'pointer' } : undefined}
      onClick={onEdit && !editing ? () => onEdit(task) : undefined}
    >
      <td className="ps-0 align-middle" style={{ width: 24 }}>
        <span
          className="text-muted align-middle"
          draggable
          onClick={stop}
          onDragStart={(e) => {
            e.stopPropagation();
            // If this task is part of a multi-selection, drag them all.
            const ids = selected && selectedIds && selectedIds.length > 1 ? selectedIds : [task.id];
            e.dataTransfer.setData('application/x-focusly-task', JSON.stringify(ids));
            e.dataTransfer.setData('text/plain', ids.length > 1 ? `${ids.length} tasks` : (task.name || ''));
            e.dataTransfer.effectAllowed = 'move';
          }}
          title="Drag onto a folder to move this task"
          aria-label="Drag task to another folder"
          role="button"
          style={{ cursor: 'grab', fontSize: '0.95rem', lineHeight: 1 }}
        >
          {'⠿'}
        </span>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>Task</span>
      </td>
      <td><small className="text-muted">{task.task_number}</small></td>
      <td>
        {editing === 'name' ? (
          <InlineText
            value={task.name}
            onCommit={apply('name')}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <div
            className="fw-semibold"
            onClick={onApplyField ? (e) => { stop(e); setEditing('name'); } : undefined}
            style={onApplyField ? { cursor: 'text' } : undefined}
          >
            {task.name}
          </div>
        )}
      </td>
      {showFolder && <td><small>{task.folder_name}</small></td>}
      <td onClick={onApplyField ? (e) => { stop(e); setEditing('priority'); } : undefined}>
        {editing === 'priority' ? (
          <InlineSelect
            options={PRIORITY_OPTIONS}
            value={task.priority}
            onCommit={apply('priority')}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <span className={`badge ${priorityBadge[task.priority]}`}>{task.priority}</span>
        )}
      </td>
      <td onClick={onApplyField ? (e) => { stop(e); setEditing('status'); } : undefined}>
        {editing === 'status' ? (
          <InlineSelect
            options={STATUS_OPTIONS}
            value={task.status}
            onCommit={apply('status')}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <span className={`badge ${statusBadge[task.status]}`}>{task.status}</span>
        )}
      </td>
      <td onClick={onApplyField ? (e) => { stop(e); setEditing('start_date'); } : undefined}>
        {editing === 'start_date' ? (
          <InlineDate
            value={task.start_date}
            onCommit={apply('start_date')}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <small>{task.start_date ? formatDateLocal(task.start_date) : '-'}</small>
        )}
      </td>
      <td onClick={onApplyField ? (e) => { stop(e); setEditing('end_date'); } : undefined}>
        {editing === 'end_date' ? (
          <InlineDate
            value={task.end_date}
            onCommit={apply('end_date')}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <small>{task.end_date ? formatDateLocal(task.end_date) : '-'}</small>
        )}
      </td>
      <td onClick={onDelete ? stop : undefined}>
        {onDelete && (
          <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(task.id)}>Delete</button>
        )}
      </td>
      {selectable && (
        <td className="text-center" onClick={stop}>
          <input
            type="checkbox"
            className="form-check-input"
            checked={!!selected}
            onChange={() => onToggleSelect(task.id)}
          />
        </td>
      )}
    </tr>
  );
}
